import { isPlainObject, type McpAppPayload } from "./payload";

/**
 * Host side of the MCP Apps postMessage bridge for the web concierge (GON-7817).
 *
 * One bridge serves one view frame. It only accepts JSON-RPC from that frame's window, only
 * replies to it, and exposes a deliberately small surface: the initial tool data, the search
 * view's own search tool, a short user chat message, and frame sizing. Fullscreen is declined and
 * model-context updates are acknowledged but not forwarded yet.
 */

export type JsonRpcId = string | number;

export interface JsonRpcMessage {
  jsonrpc: "2.0";
  id?: JsonRpcId;
  method?: string;
  params?: unknown;
  result?: unknown;
  error?: { code: number; message: string };
}

export interface McpToolResult {
  content: unknown[];
  structuredContent?: Record<string, unknown> | null;
  isError: boolean;
}

export interface HostBridgeOptions {
  target: { postMessage(message: JsonRpcMessage, targetOrigin: string): void };
  payload: McpAppPayload;
  toolArguments?: Record<string, unknown>;
  callTool: (
    name: string,
    args: Record<string, unknown>
  ) => Promise<McpToolResult>;
  onSendMessage?: (text: string) => void;
  onHeight: (height: number) => void;
  /** True while the chat cannot take a new message. */
  isBusy: () => boolean;
  styleVariables?: Record<string, string>;
  now?: () => number;
}

export interface HostBridge {
  handleMessage(event: { source: unknown; data: unknown }): void;
  teardown(): void;
}

const PROTOCOL_VERSION = "2026-01-26";
// Only the search view is interactive, and only with its own search tool.
const CALLABLE_TOOL = "search_hotels_v2";
const MAX_MESSAGE_LENGTH = 500;
const MESSAGE_INTERVAL_MS = 2000;
export const MIN_FRAME_HEIGHT = 120;
export const MAX_FRAME_HEIGHT = 1200;

const METHOD_NOT_FOUND = -32601;
const INVALID_PARAMS = -32602;
const SERVER_ERROR = -32000;

// View variable -> Gondola design token (HSL channels).
const STYLE_TOKENS: Record<string, string> = {
  "--color-background-primary": "--card",
  "--color-background-secondary": "--muted",
  "--color-text-primary": "--foreground",
  "--color-text-secondary": "--muted-foreground",
  "--color-border-primary": "--border",
  "--color-background-inverse": "--primary",
  "--color-text-inverse": "--primary-foreground",
  "--color-text-danger": "--destructive",
};
const HSL_CHANNELS = /^[\d.]+(deg)? [\d.]+% [\d.]+%$/;

/** Resolves the host's design tokens into the view's standard style variables. */
export function hostStyleVariables(
  readToken: (name: string) => string
): Record<string, string> {
  const variables: Record<string, string> = {};
  for (const [variable, token] of Object.entries(STYLE_TOKENS)) {
    const value = readToken(token).trim();
    if (HSL_CHANNELS.test(value)) variables[variable] = `hsl(${value})`;
  }
  return variables;
}

function isJsonRpc(data: unknown): data is JsonRpcMessage {
  return isPlainObject(data) && data.jsonrpc === "2.0";
}

function isId(value: unknown): value is JsonRpcId {
  return typeof value === "string" || typeof value === "number";
}

function messageText(params: unknown): string | null {
  if (!isPlainObject(params) || params.role !== "user") return null;
  const content = params.content;
  if (!isPlainObject(content) || content.type !== "text") return null;
  if (typeof content.text !== "string") return null;
  const text = content.text.trim();
  return text && text.length <= MAX_MESSAGE_LENGTH ? text : null;
}

export function createHostBridge(options: HostBridgeOptions): HostBridge {
  const now = options.now ?? Date.now;
  let closed = false;
  let delivered = false;
  let lastMessageAt = -Infinity;

  const post = (message: Omit<JsonRpcMessage, "jsonrpc">) => {
    if (!closed)
      options.target.postMessage({ jsonrpc: "2.0", ...message }, "*");
  };
  const respond = (id: JsonRpcId, result: unknown) => post({ id, result });
  const fail = (id: JsonRpcId, code: number, message: string) =>
    post({ id, error: { code, message } });

  function deliverToolData() {
    if (delivered) return;
    delivered = true;
    post({
      method: "ui/notifications/tool-input",
      params: { arguments: options.toolArguments ?? {} },
    });
    post({
      method: "ui/notifications/tool-result",
      params: {
        content: [],
        structuredContent: options.payload.structuredContent,
      },
    });
  }

  function callTool(id: JsonRpcId, params: unknown) {
    const name = isPlainObject(params) ? params.name : undefined;
    const args = isPlainObject(params) ? params.arguments : undefined;
    if (
      name !== CALLABLE_TOOL ||
      options.payload.toolName !== CALLABLE_TOOL ||
      !isPlainObject(args)
    ) {
      fail(id, INVALID_PARAMS, "Tool not available");
      return;
    }
    options.callTool(name, args).then(
      (result) =>
        respond(id, {
          content: result.content,
          structuredContent: result.structuredContent ?? null,
          isError: result.isError,
        }),
      () => fail(id, SERVER_ERROR, "Search failed")
    );
  }

  function sendMessage(id: JsonRpcId, params: unknown) {
    const text = messageText(params);
    if (text === null) {
      fail(id, INVALID_PARAMS, "Unsupported message");
      return;
    }
    const at = now();
    if (
      !options.onSendMessage ||
      options.isBusy() ||
      at - lastMessageAt < MESSAGE_INTERVAL_MS
    ) {
      fail(id, SERVER_ERROR, "Chat is busy");
      return;
    }
    lastMessageAt = at;
    options.onSendMessage(text);
    respond(id, {});
  }

  function handleRequest(id: JsonRpcId, method: string, params: unknown) {
    switch (method) {
      case "ui/initialize":
        respond(id, {
          protocolVersion:
            isPlainObject(params) && typeof params.protocolVersion === "string"
              ? params.protocolVersion
              : PROTOCOL_VERSION,
          hostInfo: { name: "Gondola", version: "1.0.0" },
          hostCapabilities: {},
          hostContext: {
            theme: "dark",
            displayMode: "inline",
            availableDisplayModes: ["inline"],
            styles: { variables: options.styleVariables ?? {} },
          },
        });
        return;
      case "tools/call":
        callTool(id, params);
        return;
      case "ui/message":
        sendMessage(id, params);
        return;
      case "ui/update-model-context":
        respond(id, {});
        return;
      case "ui/request-display-mode":
        respond(id, { mode: "inline" });
        return;
      default:
        fail(id, METHOD_NOT_FOUND, "Method not found");
    }
  }

  function handleNotification(method: string, params: unknown) {
    if (method === "ui/notifications/initialized") {
      deliverToolData();
      return;
    }
    if (method === "ui/notifications/size-changed") {
      const height = isPlainObject(params) ? params.height : undefined;
      if (typeof height !== "number" || !Number.isFinite(height)) return;
      options.onHeight(
        Math.min(
          MAX_FRAME_HEIGHT,
          Math.max(MIN_FRAME_HEIGHT, Math.round(height))
        )
      );
    }
  }

  return {
    handleMessage(event) {
      if (closed || event.source !== options.target) return;
      const data = event.data;
      if (!isJsonRpc(data) || typeof data.method !== "string") return;
      if (isId(data.id)) handleRequest(data.id, data.method, data.params);
      else handleNotification(data.method, data.params);
    },
    teardown() {
      if (closed) return;
      post({ id: "host-teardown", method: "ui/resource-teardown", params: {} });
      closed = true;
    },
  };
}
